import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { message } from "antd";
import { CopyOutlined, HighlightOutlined, PictureOutlined, DeleteOutlined } from "@ant-design/icons";
import type { LoadedVerse } from "../services/bible";
import type { RecoveryFootnoteMark } from "../services/recovery";

interface Props {
  verse: LoadedVerse;
  versionIds: string[];
  text?: string;
  footnotes?: RecoveryFootnoteMark[];
  onOpenFootnote?: (mark: RecoveryFootnoteMark) => void;
  isPrologue?: boolean;
  mergedNote?: string | null;
  highlighted: boolean;
  speaking?: boolean;
  onToggleHighlight: () => void;
}

interface MenuState {
  x: number;
  y: number;
}

const LONG_PRESS_MS = 600;
const MOVE_TOLERANCE = 10;

const VerseBlock: React.FC<Props> = ({
  verse,
  versionIds,
  text,
  footnotes,
  onOpenFootnote,
  isPrologue = false,
  mergedNote = null,
  highlighted,
  speaking = false,
  onToggleHighlight,
}) => {
  const intl = useIntl();
  const [menu, setMenu] = useState<MenuState | null>(null);
  const displayText = text ?? verse.Text;
  const justOpenedRef = useRef(false);
  const longPressTimerRef = useRef<number | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const close = useCallback(() => {
    if (justOpenedRef.current) {
      // 忽略长按弹出后紧接而来的 touchend → click，避免菜单被立刻关闭
      justOpenedRef.current = false;
      return;
    }
    setMenu(null);
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback((x: number, y: number) => {
    justOpenedRef.current = true;
    window.setTimeout(() => {
      justOpenedRef.current = false;
    }, 500);
    setMenu({ x, y });
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openMenu(e.clientX, e.clientY);
    },
    [openMenu]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      longPressTimerRef.current = window.setTimeout(() => {
        touchStartRef.current = null;
        openMenu(touch.clientX, touch.clientY);
        try {
          navigator.vibrate?.(10);
        } catch {}
      }, LONG_PRESS_MS);
    },
    [openMenu]
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!longPressTimerRef.current || !touchStartRef.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    // 允许 10px 移动容差，防止手指微颤取消长按
    if (Math.abs(dx) > MOVE_TOLERANCE || Math.abs(dy) > MOVE_TOLERANCE) {
      if (longPressTimerRef.current) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      touchStartRef.current = null;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  }, []);

  // 滚动、失焦或按 Esc 时关闭菜单
  useEffect(() => {
    if (!menu) return;
    const handleScroll = () => close();
    const handleBlur = () => close();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("scroll", handleScroll, { capture: true });
    window.addEventListener("blur", handleBlur);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [menu, close]);

  const handleCopy = useCallback(async () => {
    const copyText = `${verse.reference}  ${displayText.replace(/^\d+\s*/, "")}`;
    try {
      await navigator.clipboard.writeText(copyText);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = copyText;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    message.success(intl.formatMessage({ id: "verse.copied" }));
    close();
  }, [verse, displayText, intl, close]);

  const handleImage = useCallback(() => {
    // 与 holybible 一致：打开 LuminaVerse 的经文美图页（同一 aliving.stream 域名）。
    // LuminaVerse 按 /bookId/chapter/verse 解析，bookId 必须是整本圣经 1-66 的卷序号（如诗篇=19），不能传书卷名。
    const bookId = verse.bookNumber || verse.Book;
    window.open(
      `https://lv.aliving.stream/${bookId}/${verse.Chapter}/${verse.Verse}?v=${versionIds.join(",")}`,
      "_blank"
    );
    close();
  }, [verse, versionIds, close]);

  const handleHighlight = useCallback(() => {
    onToggleHighlight();
    close();
  }, [onToggleHighlight, close]);

  const menuStyle = menu
    ? {
        left: Math.min(menu.x, window.innerWidth - 180),
        top: Math.min(menu.y, window.innerHeight - 160),
      }
    : undefined;

  const renderText = () => {
    if (!footnotes || footnotes.length === 0) {
      return <span className="verse-text">{displayText}</span>;
    }
    const nodes: React.ReactNode[] = [];
    let last = 0;
    footnotes.forEach((fn, i) => {
      const pos = fn.location - 1;
      if (pos > last) nodes.push(<span key={`t-${i}`}>{displayText.slice(last, pos)}</span>);
      nodes.push(
        <sup
          key={`fn-${i}`}
          className="footnote-num"
          onClick={(e) => {
            e.stopPropagation();
            onOpenFootnote?.(fn);
          }}
        >
          {fn.seq}
        </sup>
      );
      last = pos;
    });
    if (last < displayText.length) nodes.push(<span key="t-end">{displayText.slice(last)}</span>);
    return <span className="verse-text">{nodes}</span>;
  };

  if (mergedNote) {
    return (
      <div className="verse-block verse-merged-note">
        <span className="verse-merge-label">{mergedNote}</span>
      </div>
    );
  }

  return (
    <>
      <div
        id={`verse-${verse.Book}|${verse.Chapter}|${verse.Verse}`}
        className={`verse-block ${highlighted ? "highlighted" : ""} ${speaking ? "speaking" : ""} ${isPrologue ? "verse-prologue" : ""}`}
        onContextMenu={handleContextMenu}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {!isPrologue && <span className="verse-num">{verse.Verse}</span>}
        {renderText()}
      </div>
      {menu && (
        <>
          <div className="verse-context-menu-overlay" onClick={close} />
          <div className="verse-context-menu" style={menuStyle}>
            <div className="menu-item" onClick={handleCopy}>
              <CopyOutlined /> {intl.formatMessage({ id: "verse.copy" })}
            </div>
            <div className="menu-item" onClick={handleHighlight}>
              {highlighted ? <DeleteOutlined /> : <HighlightOutlined />}{" "}
              {intl.formatMessage({ id: highlighted ? "verse.unhighlight" : "verse.highlight" })}
            </div>
            <div className="menu-item" onClick={handleImage}>
              <PictureOutlined /> {intl.formatMessage({ id: "verse.image" })}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default VerseBlock;
